import { AnyAction } from "@reduxjs/toolkit";
import { Dispatch, useCallback } from "react";
import { FailureTypeValue, MethodSetupRequest, UuidType } from "../../../types";
import { PaymentContextValue } from "../../payment-context";
import { setError, setPaymentsMethodState } from "../state";
import {
  CreatePaymentMethodResultType,
  PaymentsClientContextProps,
} from "../payments-client-context";
import {
  decodeCommonError,
  handleFailureResultObject,
  handleSuccessResultObject,
  logErrorMessage,
} from "../../../utils";
import { PaymentMethodCoordinatorJSClient } from "../clients";
import { enTextTemplates } from "../../../assets/i18n/en-text-templates";

const CREATE_PAYMENT_METHOD_API_ERRORS = {
  SETUP_INTENT_NOT_FOUND: "PMC_BLE_0011",
};

export type UseCreatePaymentMethodProps = {
  dispatch: Dispatch<AnyAction>;
  client: PaymentMethodCoordinatorJSClient | undefined;
  uuid?: UuidType;
} & Pick<
  PaymentContextValue,
  "onFailure" | "onResponse" | "onSuccess" | "state"
> &
  Pick<PaymentsClientContextProps, "localizedText">;

export const useCreatePaymentMethod = ({
  dispatch,
  client,
  uuid,
  onFailure,
  onSuccess,
  onResponse,
  state,
  localizedText,
}: UseCreatePaymentMethodProps): ((
  requestData: MethodSetupRequest
) => Promise<CreatePaymentMethodResultType>) => {
  const activePaymentMethod = state.internalState?.activePaymentMethod;

  const handleCreatePaymentMethodException = useCallback(
    (ex) => {
      const decodedError = decodeCommonError(ex);
      const errorStatusCode = ex?.lastError?.response?.status;
      const errorObject = {
        isError: true,
        message: errorStatusCode
          ? localizedText(
              `ERROR_STATUS_CODE_${errorStatusCode}` as keyof typeof enTextTemplates
            )
          : localizedText(`NETWORK_ERROR`),
      };
      const responseObject = {
        message: errorObject.message,
        decodedError: decodedError,
        rawError: ex,
      };
      const errorResult = handleFailureResultObject(
        activePaymentMethod,
        responseObject
      );

      switch (errorStatusCode) {
        case 404:
          if (
            decodedError!.find(
              ({ code }) =>
                code === CREATE_PAYMENT_METHOD_API_ERRORS.SETUP_INTENT_NOT_FOUND
            )
          ) {
            onFailure(errorResult);
            return {
              error: {
                type: FailureTypeValue.REDIS_CACHE_404_ERROR,
              },
            };
          } else {
            onFailure(errorResult);
            dispatch(setError(errorObject));
          }
          break;
        default:
          dispatch(setError(errorObject));
          onFailure(errorResult);
          break;
      }
      logErrorMessage(uuid);
      return { error: errorResult };
    },
    [onFailure, localizedText, dispatch, uuid, activePaymentMethod]
  );

  const createPaymentMethod = useCallback(
    async (
      requestData: MethodSetupRequest
    ): Promise<CreatePaymentMethodResultType> => {
      let result = undefined;
      try {
        result = await client?.createPaymentMethod(requestData);
        result?.data;
        dispatch(setPaymentsMethodState(result?.data));

        const resultObject = handleSuccessResultObject(
          activePaymentMethod,

          result?.data
        );

        onSuccess(resultObject);
        onResponse(resultObject);
        return {
          result: result?.data,
          success: {
            type: resultObject.type,
          },
        };
      } catch (ex) {
        return handleCreatePaymentMethodException(ex);
      }
    },

    [
      client,
      onSuccess,
      onResponse,
      dispatch,
      handleCreatePaymentMethodException,
      activePaymentMethod,
    ]
  );
  return createPaymentMethod;
};
